// The date control: a trigger styled like Input with a calendar popover.
// Keyboard is the ARIA grid pattern: arrows by day and week, PageUp and
// PageDown by month, Enter selects, Escape closes. value is 'YYYY-MM-DD' or
// '' and onChange receives the same shape. Optional label, help, and error
// render the same shell as Input.

import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { FieldShell } from '../FieldShell/index.jsx'
import { useOutsideClose } from '../useOutsideClose.js'
import './datefield.css'

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

// A calendar day as parts, never as an instant, so no timezone can shift it.
const toParts = (value) => {
  if (!value) return null
  const [year, month, day] = value.split('-').map(Number)
  return { year, month: month - 1, day }
}
const toValue = ({ year, month, day }) =>
  `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
const daysIn = (year, month) => new Date(year, month + 1, 0).getDate()

function formatDisplay(value) {
  const parts = toParts(value)
  if (!parts) return null
  return new Date(parts.year, parts.month, parts.day).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function DateField({ id, value, onChange, label, help, error, ariaLabel }) {
  const reactId = useId()
  const baseId = id || reactId
  const wrapRef = useRef(null)
  const triggerRef = useRef(null)
  const gridRef = useRef(null)
  const [open, setOpen] = useState(false)
  const today = useMemo(() => {
    const now = new Date()
    return { year: now.getFullYear(), month: now.getMonth(), day: now.getDate() }
  }, [])
  const [cursor, setCursor] = useState(toParts(value) ?? today)

  useOutsideClose(wrapRef, open, () => setOpen(false))

  function openCalendar() {
    setCursor(toParts(value) ?? today)
    setOpen(true)
  }

  function choose(parts) {
    onChange(toValue(parts))
    setOpen(false)
    triggerRef.current?.focus()
  }

  function move(days) {
    const date = new Date(cursor.year, cursor.month, cursor.day + days)
    setCursor({ year: date.getFullYear(), month: date.getMonth(), day: date.getDate() })
  }

  function moveMonth(step) {
    const date = new Date(cursor.year, cursor.month + step, 1)
    const clamped = Math.min(cursor.day, daysIn(date.getFullYear(), date.getMonth()))
    setCursor({ year: date.getFullYear(), month: date.getMonth(), day: clamped })
  }

  // Focus follows the cursor day while the grid is open. preventScroll
  // matters: scrolling a clipped popover to reveal the focused day would
  // shift the whole grid sideways.
  useEffect(() => {
    if (!open) return
    gridRef.current?.querySelector('[tabindex="0"]')?.focus({ preventScroll: true })
  }, [open, cursor])

  function onGridKeyDown(event) {
    const keys = {
      ArrowLeft: () => move(-1),
      ArrowRight: () => move(1),
      ArrowUp: () => move(-7),
      ArrowDown: () => move(7),
      PageUp: () => moveMonth(-1),
      PageDown: () => moveMonth(1),
      Home: () => setCursor({ ...cursor, day: 1 }),
      End: () => setCursor({ ...cursor, day: daysIn(cursor.year, cursor.month) }),
    }
    if (keys[event.key]) {
      event.preventDefault()
      keys[event.key]()
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      choose(cursor)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
      triggerRef.current?.focus()
    }
  }

  const firstWeekday = new Date(cursor.year, cursor.month, 1).getDay()
  const total = daysIn(cursor.year, cursor.month)
  const selectedParts = toParts(value)
  const isSame = (a, b) => a && b && a.year === b.year && a.month === b.month && a.day === b.day

  const cells = []
  for (let blank = 0; blank < firstWeekday; blank += 1) cells.push(null)
  for (let day = 1; day <= total; day += 1)
    cells.push({ year: cursor.year, month: cursor.month, day })

  const display = formatDisplay(value)

  const control = (
    <div className="ui-select" ref={wrapRef}>
      <button
        ref={triggerRef}
        type="button"
        id={baseId}
        className={`ui-select-trigger${display ? '' : ' is-placeholder'}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-invalid={error ? true : undefined}
        onClick={() => (open ? setOpen(false) : openCalendar())}
        onKeyDown={(event) => {
          if (!open && ['ArrowDown', 'Enter', ' '].includes(event.key)) {
            event.preventDefault()
            openCalendar()
          }
        }}
      >
        <span className="ui-select-value">{display ?? 'Choose a date'}</span>
        <span className="ui-select-caret" aria-hidden="true" />
      </button>

      {open ? (
        <div className="ui-popover ui-datefield-pop" role="dialog" aria-label="Choose a date">
          <div className="ui-datefield-head">
            <button
              type="button"
              className="ui-btn ui-btn-ghost ui-datefield-nav"
              onClick={() => moveMonth(-1)}
              aria-label="Previous month"
            >
              &#8592;
            </button>
            <span className="ui-datefield-month" aria-live="polite">
              {MONTHS[cursor.month]} {cursor.year}
            </span>
            <button
              type="button"
              className="ui-btn ui-btn-ghost ui-datefield-nav"
              onClick={() => moveMonth(1)}
              aria-label="Next month"
            >
              &#8594;
            </button>
          </div>

          <div className="ui-datefield-grid" role="grid" ref={gridRef} onKeyDown={onGridKeyDown}>
            {WEEKDAYS.map((weekday) => (
              <span key={weekday} className="ui-datefield-weekday" aria-hidden="true">
                {weekday}
              </span>
            ))}
            {cells.map((cell, index) =>
              cell ? (
                <button
                  key={toValue(cell)}
                  type="button"
                  className={`ui-datefield-day${isSame(cell, selectedParts) ? ' is-selected' : ''}${
                    isSame(cell, today) ? ' is-today' : ''
                  }`}
                  tabIndex={isSame(cell, cursor) ? 0 : -1}
                  aria-pressed={isSame(cell, selectedParts)}
                  onClick={() => choose(cell)}
                >
                  {cell.day}
                </button>
              ) : (
                <span key={`blank-${index}`} aria-hidden="true" />
              ),
            )}
          </div>

          <div className="ui-datefield-foot">
            <button
              type="button"
              className="ui-btn ui-btn-ghost ui-datefield-nav"
              onClick={() => choose(today)}
            >
              Today
            </button>
            <span style={{ flex: 1 }} />
            {value ? (
              <button
                type="button"
                className="ui-btn ui-btn-ghost ui-datefield-nav"
                onClick={() => {
                  onChange('')
                  setOpen(false)
                  triggerRef.current?.focus()
                }}
              >
                Clear
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )

  return (
    <FieldShell id={baseId} label={label} help={help} error={error}>
      {control}
    </FieldShell>
  )
}
